import { QueryObserver } from '@tanstack/query-core';

import {
  readDriversData,
  readDriversOrder,
  readFormulaDriver,
  readFormulaOptions,
  readFreestyleDriver
} from '../../predict/src/core/api/non-financial-drivers';
import { queryClient } from '../core/queryClient';

// -------------------------
// Formula Options
// -------------------------

export type FetchFormulaOptionsResult = Awaited<ReturnType<typeof readFormulaOptions>>;

export const formulaOptionsQueryKey = (workspaceId: string, organisationId: string) => [
  'non-financial-driver-formula-options',
  workspaceId,
  organisationId
];

export const formulaOptionsQuery = (
  workspaceId: string,
  organisationId: string
): QueryObserver<FetchFormulaOptionsResult> =>
  new QueryObserver<FetchFormulaOptionsResult>(queryClient, {
    queryKey: formulaOptionsQueryKey(workspaceId, organisationId),
    queryFn: () => readFormulaOptions(workspaceId, organisationId)
  });

// -------------------------
// Drivers Data
// -------------------------

export type FetchDriversDataResult = Awaited<ReturnType<typeof readDriversData>>;

export const driversDataQueryKey = (workspaceId: string, organisationId: string) => [
  'non-financial-drivers-data',
  workspaceId,
  organisationId
];

export const driversDataQuery = (workspaceId: string, organisationId: string): QueryObserver<FetchDriversDataResult> =>
  new QueryObserver<FetchDriversDataResult>(queryClient, {
    queryKey: driversDataQueryKey(workspaceId, organisationId),
    queryFn: () => readDriversData(workspaceId, organisationId)
  });

// -------------------------
// Drivers Order
// -------------------------

export type FetchDriversOrderResult = Awaited<ReturnType<typeof readDriversOrder>>;

export const driversOrderQueryKey = (workspaceId: string, organisationId: string) => [
  'non-financial-drivers-order',
  workspaceId,
  organisationId
];

export const driversOrderQuery = (
  workspaceId: string,
  organisationId: string
): QueryObserver<FetchDriversOrderResult> =>
  new QueryObserver<FetchDriversOrderResult>(queryClient, {
    queryKey: driversOrderQueryKey(workspaceId, organisationId),
    queryFn: () => readDriversOrder(workspaceId, organisationId)
  });

// -------------------------
// Driver Detail
// -------------------------

export type FetchFormulaDriverDetailResult = Awaited<ReturnType<typeof readFormulaDriver>>;
export type FetchFreestyleDriverDetailResult = Awaited<ReturnType<typeof readFreestyleDriver>>;

export const driverDetailQueryKey = (workspaceId: string, organisationId: string, driverId: string) => [
  'non-financial-driver-detail',
  workspaceId,
  organisationId,
  driverId
];

export const formulaDriverQueryConfig = (workspaceId: string, organisationId: string, driverId: string) => ({
  queryKey: driverDetailQueryKey(workspaceId, organisationId, driverId),
  queryFn: () => readFormulaDriver(workspaceId, organisationId, driverId)
});

export const freestyleDriverQueryConfig = (workspaceId: string, organisationId: string, driverId: string) => ({
  queryKey: driverDetailQueryKey(workspaceId, organisationId, driverId),
  queryFn: () => readFreestyleDriver(workspaceId, organisationId, driverId)
});

export const formulaDriverDetailQuery = (): QueryObserver<FetchFormulaDriverDetailResult> =>
  new QueryObserver<FetchFormulaDriverDetailResult>(queryClient, {
    enabled: false,
    queryKey: ['non-financial-formula-driver-detail-disabled'],
    queryFn: () => Promise.resolve((undefined as unknown) as FetchFormulaDriverDetailResult)
  });

export const freestyleDriverDetailQuery = (): QueryObserver<FetchFreestyleDriverDetailResult> =>
  new QueryObserver<FetchFreestyleDriverDetailResult>(queryClient, {
    enabled: false,
    queryKey: ['non-financial-freestyle-driver-detail-disabled'],
    queryFn: () => Promise.resolve((undefined as unknown) as FetchFreestyleDriverDetailResult)
  });
